import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utilities/axiosInstance";

const initialState = {
  data: [],
  itemsCount: 0,
  pages: 0,
  isLoading: false,
  error: null,
};

export const getPartnerVerifications = createAsyncThunk(
  "partnerVerification/getPartnerVerifications",
  async ({ page = 1, size = 10, query = "", filter = {} }, thunkApi) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (page) params.append("page", page);
      if (size) params.append("size", size);
      if (query) params.append("search", query);

      // Add filter parameters
      Object.keys(filter).forEach((key) => {
        if (filter[key]) {
          params.append(key, filter[key]);
        }
      });

      const response = await axiosInstance.get(
        `/partners?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message || "Failed to fetch partner verifications"
      );
    }
  }
);

export const updatePartnerVerification = createAsyncThunk(
  "partnerVerification/updatePartnerVerification",
  async ({ id, data }, thunkApi) => {
    try {
      const response = await axiosInstance.put(`/partners/${id}`, data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message || "Failed to update partner verification"
      );
    }
  }
);

export const createPartnerVerification = createAsyncThunk(
  "partnerVerification/createPartnerVerification",
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance.post(`/partners`, data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message || "Failed to create partner verification"
      );
    }
  }
);

export const checkPartnerVerification = createAsyncThunk(
  "partnerVerification/checkPartnerVerification",
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance.get(`/partners/check`);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message || "Failed to check partner verification"
      );
    }
  }
);

const partnerVerificationSlice = createSlice({
  name: "partnerVerification",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPartnerVerifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPartnerVerifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data?.data || [];
        state.itemsCount = action.payload.data?.itemsCount || 0;
        state.pages = action.payload.data?.pages || 0;
      })
      .addCase(getPartnerVerifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updatePartnerVerification.pending, (state) => {
        state.isLoading = true;
        // state.error = null;
      })
      .addCase(updatePartnerVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the specific item in the data array
        const index = state.data.findIndex(
          (item) => item._id === action.payload.data._id
        );
        if (index !== -1) {
          state.data[index] = action.payload.data;
        }
      })
      .addCase(updatePartnerVerification.rejected, (state, action) => {
        state.isLoading = false;
        // state.error = action.payload;
      })
      .addCase(createPartnerVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPartnerVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add the new item to the data array
        state.data.unshift(action.payload.data);
        state.itemsCount += 1;
      })
      .addCase(createPartnerVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(checkPartnerVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkPartnerVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle check response if needed
      })
      .addCase(checkPartnerVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = partnerVerificationSlice.actions;
export default partnerVerificationSlice.reducer;
